import React, { useState, useEffect, useCallback } from "react";
import { Route, Routes, useNavigate, useLocation, useParams, Navigate } from "react-router-dom";
import { FactDetailContainer } from "./FactDetail.styled";
import { Overview, Suggestions } from "./pages";
import { Header, Tabs } from "./components";
import { FactDetailTabsEnum } from "./enums/factDetailTabs";
import { useFactDetail } from "./hooks";
import { FactDetailProvider, FactDetailSuggestionsProvider } from "./context";

const FactDetailContent = ({ breadcrumbHelpers, factId, knowledgeId, onAddSuggestion }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(FactDetailTabsEnum.OVERVIEW);
  const { factData } = useFactDetail(factId);

  const getTabRoute = (tabIndex) => {
    switch (tabIndex) {
      case FactDetailTabsEnum.OVERVIEW:
        return "overview";
      case FactDetailTabsEnum.SUGGESTIONS:
        return "suggestions";
      default:
        return "overview";
    }
  };

  const getTabFromRoute = useCallback((pathname) => {
    const pathParts = pathname.split('/').filter(Boolean);
    const factIndex = pathParts.indexOf('facts');
    
    if (factIndex === -1) {
      return FactDetailTabsEnum.OVERVIEW;
    }
    
    // Look for segment after factId
    const factIdSegment = factId;
    const factIdIndex = pathParts.findIndex(part => part === factIdSegment);
    
    if (factIdIndex === -1) {
      return FactDetailTabsEnum.OVERVIEW;
    }
    
    const segmentAfterFactId = pathParts[factIdIndex + 1];
    
    if (segmentAfterFactId === "overview") {
      return FactDetailTabsEnum.OVERVIEW;
    } else if (segmentAfterFactId === "suggestions") {
      return FactDetailTabsEnum.SUGGESTIONS;
    } else {
      return FactDetailTabsEnum.OVERVIEW;
    }
  }, [factId]);

  useEffect(() => {
    const tabFromUrl = getTabFromRoute(location.pathname);
    setSelectedTab(tabFromUrl);
    
    // If no tab segment, navigate to overview
    const pathParts = location.pathname.split('/').filter(Boolean);
    const factIdIndex = pathParts.findIndex(part => part === factId);
    
    if (factIdIndex !== -1) {
      const segmentAfterFactId = pathParts[factIdIndex + 1];
      
      // Only redirect if we're at the factId level without a tab segment
      // Don't redirect if we're already on a valid tab
      if (!segmentAfterFactId || (segmentAfterFactId !== "overview" && segmentAfterFactId !== "suggestions")) {
        // Only navigate if we're not already navigating (prevent loops)
        const currentPath = location.pathname;
        const expectedPath = `/v2/knowledge/knowledge-hub/${knowledgeId}/facts/${factId}/overview`;
        if (currentPath !== expectedPath && !currentPath.includes('/overview') && !currentPath.includes('/suggestions')) {
          navigate(expectedPath, { replace: true });
        }
      }
    }
  }, [location.pathname, navigate, knowledgeId, factId, getTabFromRoute]);

  useEffect(() => {
    const tabFromUrl = getTabFromRoute(location.pathname);
    const tabLabel = tabFromUrl === FactDetailTabsEnum.OVERVIEW 
      ? "Overview" 
      : "Suggestions";
    
    const tabRoute = getTabRoute(tabFromUrl);
    const tabHref = `/v2/knowledge/knowledge-hub/${knowledgeId}/facts/${factId}/${tabRoute}`;
    
    breadcrumbHelpers?.setPageBreadcrumb?.(null, factData?.factName || "Fact", {
      subHref: `/v2/knowledge/knowledge-hub/${knowledgeId}/facts/${factId}`,
      tabLabel: tabLabel,
      tabHref: tabHref,
    });
  }, [breadcrumbHelpers, location.pathname, factData?.factName, knowledgeId, factId, getTabFromRoute]);

  const handleTabChange = (index) => {
    setSelectedTab(index);
    const newRoute = getTabRoute(index);
    const basePath = `/v2/knowledge/knowledge-hub/${knowledgeId}/facts/${factId}`;
    
    navigate(`${basePath}/${newRoute}`, { replace: true });
  };

  const factName = factData?.factName || "Fact";

  return (
    <FactDetailContainer>
      <Header title={factName} />
      <Tabs key={selectedTab} onTabChange={handleTabChange} defaultIndex={selectedTab} />
      <Routes>
        <Route path="overview" element={<Overview factId={factId} />} />
        <Route path="suggestions" element={<Suggestions factId={factId} onAddSuggestion={onAddSuggestion} />} />
        <Route index element={<Navigate to="overview" replace />} />
      </Routes>
    </FactDetailContainer>
  );
};

const FactDetail = ({ breadcrumbHelpers, onAddSuggestion }) => {
  const { knowledgeId, factId } = useParams();

  return (
    <FactDetailProvider>
      <FactDetailSuggestionsProvider>
        <FactDetailContent 
          breadcrumbHelpers={breadcrumbHelpers} 
          factId={factId} 
          knowledgeId={knowledgeId}
          onAddSuggestion={onAddSuggestion}
        />
      </FactDetailSuggestionsProvider>
    </FactDetailProvider>
  );
};

export default FactDetail;

